import { DeviceTagName } from '@/__generated__/graphql';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { useDispatch } from '@/store';
import { ApolloClient, useApolloClient, useMutation, useSuspenseQuery } from '@apollo/client';
import GlobalModal from '@components/GlobalModal';
import ModalContent from '@components/modal/ModalContent';
import TruncatedTypography from '@components/TruncatedTypography';
import { Access } from '@features/authorization';
import { openDeviceProfileModal } from '@features/deviceProfile/slices/deviceProfileSlice';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Article, Savings } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import Fuse from 'fuse.js';
import { useMemo, useRef, useState, type FC } from 'react';
import { List, RowComponentProps } from 'react-window';
import { ADD_DEVICE_TAG } from '../graphql/mutations/addDeviceTag';
import { REMOVE_DEVICE_TAG } from '../graphql/mutations/removeDeviceTag';
import GET_UPDATE_TAGS_DEVICES_LIST, { InternalDevice } from '../graphql/queries/getDevicesList';

const ORDER_BY_OPTIONS = ['id', 'name', 'tags'] as const;

const SuspenseComponent: FC = () => {
  const client = useApolloClient();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    organizationId: null as number | null,
  });
  const [orderBy, setOrderBy] = useState<(typeof ORDER_BY_OPTIONS)[number]>('id');
  const { data } = useSuspenseQuery(GET_UPDATE_TAGS_DEVICES_LIST);

  const [addTagMutate] = useMutation(ADD_DEVICE_TAG);
  const [removeTagMutate] = useMutation(REMOVE_DEVICE_TAG);
  const dispatch = useDispatch();
  const { i18n } = useLingui();

  const openDeviceProfile = (deviceId: string) => {
    dispatch(
      openDeviceProfileModal({
        deviceId,
      }),
    );
  };

  const addTag = async (deviceId: string, tag: DeviceTagName) => {
    // ONCLICK
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    await addTagMutate({
      variables: {
        input: {
          deviceId,
          tag,
        },
      },
    });
    // ONCOMPLETE
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // DEBOUNCE
    timeoutRef.current = setTimeout(() => {
      deleteCache(client);
    }, 1000);
  };

  const removeTag = async (deviceId: string, tag: DeviceTagName) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    await removeTagMutate({
      variables: {
        input: {
          deviceId,
          tag,
        },
      },
    });
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      deleteCache(client);
    }, 1000);
  };

  const filteredDevices = useMemo(() => {
    let filteredItems = [...data.devices];

    if (orderBy === 'tags') {
      filteredItems.sort((a, b) => {
        return b.profile.tags.length - a.profile.tags.length;
      });
    }

    if (orderBy === 'name') {
      filteredItems.sort((a, b) => {
        const nameA = a.profile.name;
        const nameB = b.profile.name;
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });
    }

    if (filters.organizationId != null) {
      filteredItems = filteredItems.filter(
        (device) => device.profile.organization?.id === filters.organizationId,
      );
    }

    if (filters.search) {
      const fuse = new Fuse(filteredItems, {
        keys: ['id', 'profile.name'],
      });
      filteredItems = fuse.search(filters.search).map(({ item }) => item);
    }

    return filteredItems;
  }, [filters, data.devices, orderBy]);

  return (
    <Box>
      <Box mb={1} display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={1}>
        <TextField
          value={filters.search}
          onChange={(event) =>
            setFilters((filters) => ({ ...filters, search: event.target.value }))
          }
          label="Buscar"
          size="small"
        />
        <StandardAutocompleteIdsWithFallback
          value={filters.organizationId}
          onChange={(_, organizationId) =>
            setFilters((filters) => ({ ...filters, organizationId }))
          }
          size="small"
          dataType="organizations"
        />
        <Autocomplete
          value={orderBy}
          disableClearable
          onChange={(_, value) => setOrderBy(value)}
          options={ORDER_BY_OPTIONS}
          getOptionLabel={(option) => {
            if (option === 'id') return 'ID';
            if (option === 'name') return t(i18n)`Nombre`;
            if (option === 'tags') return t(i18n)`Etiquetas`;
            return option;
          }}
          size="small"
          renderInput={(params) => <TextField {...params} label="Ordenar" />}
        />
      </Box>
      <Box border={1} borderColor="divider" borderRadius={1} height={550}>
        <List
          rowCount={filteredDevices.length}
          rowHeight={42}
          rowComponent={RowComponent}
          rowProps={{
            devices: filteredDevices,
            openDeviceProfileModal: openDeviceProfile,
            addTag,
            removeTag,
          }}
        />
      </Box>
    </Box>
  );
};

const RowComponent: FC<
  RowComponentProps<{
    devices: InternalDevice[];
    openDeviceProfileModal: (deviceId: string) => void;
    addTag: (deviceId: string, tag: DeviceTagName) => Promise<void>;
    removeTag: (deviceId: string, tag: DeviceTagName) => Promise<void>;
  }>
> = ({ index, style, devices, openDeviceProfileModal, addTag, removeTag }) => {
  const { i18n } = useLingui();
  const [loadingTags, setLoadingTags] = useState<DeviceTagName[]>([]);

  const device = devices[index];

  const tags = device.profile.tags.map((tag) => tag.tag);

  return (
    <Box
      style={style}
      key={device.id}
      display="flex"
      gap={2}
      borderBottom={index < devices.length - 1 ? 1 : 0}
      borderColor="divider"
      px={1}
      py="2px"
      alignItems="center"
      width="100%"
    >
      <Box flexGrow={1}>
        <Box display="flex" gap={2}>
          <TruncatedTypography fontSize="14px">{device.profile.name}</TruncatedTypography>
          <TruncatedTypography fontSize="14px" color="text.secondary">
            {device.profile.organization?.name}
          </TruncatedTypography>
        </Box>
        <TruncatedTypography fontSize="11px" color="text.secondary">
          {device.id}
        </TruncatedTypography>
      </Box>

      <Box display="flex" gap={1} alignItems="center">
        {Object.values(DeviceTagName)
          .filter((tag) => tag !== DeviceTagName.Public)
          .map((tag) => {
            const isActive = tags.some((_tag) => _tag === tag);
            const isLoading = loadingTags.some((_tag) => _tag === tag);
            const translatedLabel = tag === DeviceTagName.Billable ? <Trans>Cobrable</Trans> : tag;

            return (
              <Box
                key={tag}
                sx={{
                  position: 'relative',
                }}
              >
                <Chip
                  size="medium"
                  label={translatedLabel}
                  icon={<Savings />}
                  disabled={isLoading}
                  onClick={async () => {
                    setLoadingTags((tags) => [...tags, tag]);
                    if (isActive) await removeTag(device.id, tag);
                    else await addTag(device.id, tag);
                    setLoadingTags((tags) => tags.filter((_tag) => _tag !== tag));
                  }}
                  variant={isActive ? 'filled' : 'outlined'}
                  color={isActive ? 'primary' : 'info'}
                />
                {isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
              </Box>
            );
          })}

        <Tooltip title={t(i18n)`Abrir perfil`}>
          <IconButton size="small" onClick={() => openDeviceProfileModal(device.id)}>
            <Article fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export const UpdateMultipleDevicesTagsModal: FC = () => {
  const { globalModalProps, onClose } = useGlobalModal('updateMultipleDevicesTagsModal');

  return (
    <GlobalModal
      {...globalModalProps}
      onClose={onClose}
      dialogProps={{ maxWidth: 'lg' }}
      authorization={{
        access: Access.UpdateMultipleDevicesTagsModal,
        onClose,
      }}
      modalTitleProps={{
        title: <Trans>Actualizar etiquetas de dispositivos</Trans>,
        onClose,
      }}
    // modalActionProps={{
    //   onClose,
    // }}
    >
      <ModalContent topDivider>
        <SuspenseComponent />
      </ModalContent>
    </GlobalModal>
  );
};

const deleteCache = (client: ApolloClient<object>) => {
  client.refetchQueries({
    updateCache(cache) {
      cache.evict({ fieldName: 'devices' });
      cache.evict({ fieldName: 'devicesConnection' });
      cache.evict({ fieldName: 'devicesPageInfo' });
    },
  });
};
